import { forwardRef } from "react";
import { holePayout, formatMoney } from "@/lib/game";

/**
 * A visually distinct "poster" version of the round result, designed for
 * export as an image (Web Share / download). Rendered at 1080x1350 (portrait
 * card, IG-story friendly). Ref forwarded so the export tool can capture it.
 */
const ShareCard = forwardRef(function ShareCard(
    { players, course, holes, standings, ordered, courseLabel },
    ref,
) {
    const winner = ordered[0];
    const runnerUp = ordered[1];
    const eagleEnabled = course?.eagleEnabled ?? true;
    const totalWagered = holes.reduce(
        (a, _, i) => a + holePayout(i, holes, course?.startingBet ?? 3),
        0,
    );
    const birdieCount = holes.filter((h) => h.result?.birdie).length;
    const eagleCount = holes.filter((h) => h.result?.eagle).length;
    const tiedCount = holes.filter((h) => h.result?.status === "tied").length;

    return (
        <div
            ref={ref}
            data-testid="share-card"
            style={{
                width: 1080,
                height: 1350,
                background: "#08090A",
                color: "#FDFBF7",
                fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
                position: "relative",
                overflow: "hidden",
                padding: 64,
                boxSizing: "border-box",
                backgroundImage:
                    "radial-gradient(1000px 500px at 90% -10%, rgba(204,255,0,0.10), transparent 60%), radial-gradient(900px 500px at -10% 110%, rgba(10,92,54,0.28), transparent 60%)",
            }}
        >
            {/* Header */}
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 40,
                }}
            >
                <div
                    style={{
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: 14,
                        letterSpacing: "0.28em",
                        textTransform: "uppercase",
                        color: "#8A9198",
                    }}
                >
                    <span
                        style={{
                            display: "inline-block",
                            width: 8,
                            height: 8,
                            background: "#CCFF00",
                            marginRight: 10,
                            verticalAlign: "middle",
                        }}
                    />
                    Fairway Stakes
                </div>
                <div
                    style={{
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: 14,
                        color: "#8A9198",
                    }}
                >
                    {new Date().toLocaleDateString(undefined, {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                    })}
                </div>
            </div>

            {/* Winner block */}
            <div
                style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 12,
                    letterSpacing: "0.28em",
                    textTransform: "uppercase",
                    color: "#8A9198",
                    marginBottom: 16,
                }}
            >
                Winner takes
            </div>
            <div
                style={{
                    fontFamily:
                        "'Cabinet Grotesk', 'IBM Plex Sans', sans-serif",
                    fontWeight: 900,
                    fontSize: 148,
                    lineHeight: 0.9,
                    letterSpacing: "-0.03em",
                    color: "#CCFF00",
                    marginBottom: 16,
                }}
            >
                {winner.winnings > 0 ? "+" : ""}
                {formatMoney(winner.winnings)}
            </div>
            <div
                style={{
                    fontFamily:
                        "'Cabinet Grotesk', 'IBM Plex Sans', sans-serif",
                    fontWeight: 800,
                    fontSize: 72,
                    lineHeight: 1,
                    letterSpacing: "-0.02em",
                    textTransform: "uppercase",
                    marginBottom: 8,
                }}
            >
                {winner.name}
            </div>
            <div style={{ fontSize: 22, color: "#8A9198", marginBottom: 40 }}>
                by{" "}
                <span style={{ color: "#FDFBF7", fontWeight: 600 }}>
                    {formatMoney(winner.winnings - (runnerUp?.winnings ?? 0))}
                </span>{" "}
                over{" "}
                <span style={{ color: "#FDFBF7", fontWeight: 600 }}>
                    {runnerUp?.name || "the field"}
                </span>
            </div>

            {/* Standings */}
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "1fr",
                    gap: 12,
                    marginBottom: 40,
                }}
            >
                {ordered.map((row, i) => (
                    <div
                        key={row.idx}
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "22px 26px",
                            background: "#16181A",
                            borderLeft:
                                i === 0
                                    ? "4px solid #CCFF00"
                                    : "1px solid rgba(253,251,247,0.08)",
                            borderTop: "1px solid rgba(253,251,247,0.08)",
                            borderRight: "1px solid rgba(253,251,247,0.08)",
                            borderBottom: "1px solid rgba(253,251,247,0.08)",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 20,
                            }}
                        >
                            <div
                                style={{
                                    width: 48,
                                    height: 48,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    border: "1px solid rgba(253,251,247,0.16)",
                                    fontFamily:
                                        "'JetBrains Mono', monospace",
                                    fontSize: 18,
                                    color:
                                        i === 0 ? "#CCFF00" : "#8A9198",
                                }}
                            >
                                #{i + 1}
                            </div>
                            <div>
                                <div
                                    style={{
                                        fontFamily:
                                            "'Cabinet Grotesk', sans-serif",
                                        fontWeight: 700,
                                        fontSize: 30,
                                        textTransform: "uppercase",
                                        letterSpacing: "0.01em",
                                    }}
                                >
                                    {row.name}
                                </div>
                                <div
                                    style={{
                                        fontFamily:
                                            "'JetBrains Mono', monospace",
                                        fontSize: 13,
                                        color: "#8A9198",
                                        letterSpacing: "0.16em",
                                        textTransform: "uppercase",
                                        marginTop: 4,
                                    }}
                                >
                                    HCP{" "}
                                    <span style={{ color: "#FDFBF7" }}>
                                        {players[row.idx].handicap}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div
                            style={{
                                fontFamily: "'JetBrains Mono', monospace",
                                fontSize: 44,
                                fontWeight: 700,
                                color:
                                    row.winnings > 0
                                        ? "#CCFF00"
                                        : row.winnings < 0
                                          ? "#FF5B4A"
                                          : "#FDFBF7",
                            }}
                        >
                            {row.winnings > 0 ? "+" : ""}
                            {formatMoney(row.winnings)}
                        </div>
                    </div>
                ))}
            </div>

            {/* Stats row */}
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: `repeat(${eagleEnabled ? 4 : 3}, 1fr)`,
                    gap: 12,
                    marginBottom: 32,
                }}
            >
                {[
                    { label: "Wagered", value: `$${totalWagered}`, color: "#FDFBF7" },
                    ...(eagleEnabled
                        ? [{
                              label: "Eagles",
                              value: String(eagleCount),
                              color: "#6EE7F9",
                          }]
                        : []),
                    {
                        label: "Birdies",
                        value: String(birdieCount),
                        color: "#FFD700",
                    },
                    {
                        label: "Voided",
                        value: String(tiedCount),
                        color: "#FDFBF7",
                    },
                ].map((s) => (
                    <div
                        key={s.label}
                        style={{
                            background: "#16181A",
                            border: "1px solid rgba(253,251,247,0.08)",
                            padding: "20px 24px",
                        }}
                    >
                        <div
                            style={{
                                fontFamily:
                                    "'JetBrains Mono', monospace",
                                fontSize: 11,
                                letterSpacing: "0.28em",
                                textTransform: "uppercase",
                                color: "#8A9198",
                            }}
                        >
                            {s.label}
                        </div>
                        <div
                            style={{
                                fontFamily:
                                    "'JetBrains Mono', monospace",
                                fontSize: 36,
                                marginTop: 4,
                                color: s.color,
                            }}
                        >
                            {s.value}
                        </div>
                    </div>
                ))}
            </div>

            {/* Footer */}
            <div
                style={{
                    position: "absolute",
                    left: 64,
                    right: 64,
                    bottom: 48,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-end",
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 13,
                    color: "#8A9198",
                    letterSpacing: "0.16em",
                    textTransform: "uppercase",
                }}
            >
                <div style={{ maxWidth: "70%" }}>
                    {courseLabel ? (
                        <>
                            <div style={{ color: "#FDFBF7" }}>{courseLabel}</div>
                            <div style={{ marginTop: 6 }}>
                                18 holes · Hole 1 ${course?.startingBet ?? 3} → Hole 18 ${(course?.startingBet ?? 3) + 17} · Birdie ×2{eagleEnabled ? " · Eagle ×4" : ""}
                            </div>
                        </>
                    ) : (
                        <div>
                            18 holes · Hole 1 ${course?.startingBet ?? 3} → Hole 18 ${(course?.startingBet ?? 3) + 17} · Birdie ×2{eagleEnabled ? " · Eagle ×4" : ""}
                        </div>
                    )}
                </div>
                <div style={{ textAlign: "right", color: "#CCFF00" }}>
                    Fairway
                    <br />
                    Stakes
                </div>
            </div>
        </div>
    );
});

export default ShareCard;
